<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { vTooltip } from 'floating-vue'
import BaseAttentionBox from '@/components/ui/attentionbox/BaseAttentionBox.vue'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import type {
	ComparisonFeature,
	PageContent,
} from '@/assets/ts/pages/shipment-protection-shipping'
import { isMobile } from '@/legacy/ts/checkDevice'
import { createGAEvent, GaGeneralEvent } from '@/assets/ts/customEvent'
import { getCurrentGaPageName } from '@/lib/utils'

const props = defineProps<{
	pageContent: PageContent
	theme?: 'shopping' | 'shipping'
}>()

const { pageContent, theme = 'shopping' } = props

const getColors = (isShipping: boolean) => ({
	headerBg: isShipping ? 'bg-o-75' : 'bg-t-75',
	headerAccent: isShipping ? 'bg-o-100' : 'bg-t-100',
	categoryBg: isShipping ? 'bg-o-50' : 'bg-t-50',
	featureBg: isShipping ? 'bg-o-100' : 'bg-white',
	basicBg: isShipping ? 'bg-o-50' : 'bg-t-75',
	basicFeatureBg: 'bg-white',
	border: isShipping ? 'border-o-100' : 'border-t-100',
})

const getBorders = (isShipping: boolean, isMobileDevice: boolean) => {
	const hasBorders = isShipping && !isMobileDevice
	const borderClass = hasBorders ? 'mdp:border-x mdp:border-n-950' : ''
	const borderClassWithBottom = hasBorders
		? 'mdp:border-x mdp:border-b mdp:border-n-950'
		: ''
	const categoryBorderClass = hasBorders ? 'border-x border-n-950' : ''

	return {
		hasBorders,
		buyShipPlus: borderClass,
		buyShipPlusLast: borderClassWithBottom,
		categoryMiddle: categoryBorderClass,
	}
}

const getLayout = (shouldHideBasic: boolean) => ({
	gridCols: shouldHideBasic
		? 'grid-cols-[minmax(auto,130px)_1fr] mdp:grid-cols-[minmax(auto,280px)_1fr] lg:grid-cols-[minmax(auto,320px)_1fr]'
		: 'grid-cols-3',
	hideBasic: shouldHideBasic,
})

const getClasses = (
	isShipping: boolean,
	isMobileDevice: boolean,
	themeType: string,
) => {
	const hasBorders = isShipping && !isMobileDevice
	const borderClass = hasBorders ? 'mdp:border-x mdp:border-n-950' : ''
	const borderClassWithRecommended = hasBorders ? 'border border-n-950' : ''

	return {
		featureHeader: `${isShipping ? 'bg-o-75' : 'bg-t-75'} p-12px mdp:p-20px`,
		buyShipPlusHeader: `relative ${
			isShipping ? 'bg-o-100' : 'bg-t-100'
		} p-12px mdp:p-20px text-start flex flex-col gap-8px ${borderClass}`,
		basicHeader: `${
			isShipping ? 'bg-o-50' : 'bg-t-75'
		} p-12px mdp:p-20px text-start flex flex-col gap-8px`,
		categoryFeature: `typography-s1-emp text-n-950 ${
			isShipping ? 'bg-o-50' : 'bg-t-50'
		} p-12px mdp:p-20px flex items-center`,
		categoryMiddle: `${isShipping ? 'bg-o-50' : 'bg-t-50'} p-12px mdp:p-20px ${
			hasBorders ? 'border-x border-n-950' : ''
		}`,
		categoryBasic: `${isShipping ? 'bg-o-50' : 'bg-t-50'} p-12px mdp:p-20px`,
		featureItem: getFeatureItemClass(themeType),
		buyShipPlusFeature: `${
			isShipping ? 'bg-o-100' : 'bg-white'
		} p-12px mdp:p-20px mdp:bg-white ${borderClass}`,
		buyShipPlusFeatureLast: `${
			isShipping ? 'bg-o-100' : 'bg-white'
		} p-12px mdp:p-20px mdp:bg-white ${
			hasBorders ? 'mdp:border-x mdp:border-b mdp:border-n-950' : ''
		}`,
		basicFeature: `bg-white p-12px mdp:p-20px`,
		recommended: `typography-s2-reg absolute left-1/2 top-0 w-full -translate-x-1/2 -translate-y-full transform rounded-t-lg bg-n-950 px-8px mdp:px-24px py-4px text-center text-white ${borderClassWithRecommended}`,
	}
}

const featureItemFontClass = computed(() =>
	isMobile.value ? 'typography-s2-reg' : 'typography-s1-reg',
)

const getFeatureItemClass = (themeType: string) => {
	const baseClass = `flex items-center gap-8px p-12px sm:p-12px mdp:p-20px text-n-950 ${featureItemFontClass.value}`
	const shoppingBorder =
		'border-r border-n-100 [border-right-style:dashed] [border-image:repeating-linear-gradient(to_bottom,#e5e7eb_0,#e5e7eb_6px,transparent_6px,transparent_12px)_1]'

	return themeType === 'shopping' ? `${baseClass} ${shoppingBorder}` : baseClass
}

const config = computed(() => {
	const isShipping = theme === 'shipping'
	const isMobileDevice = isMobile.value

	return {
		colors: getColors(isShipping),
		borders: getBorders(isShipping, isMobileDevice),
		layout: getLayout(shouldHideBasicColumn.value),
		classes: getClasses(isShipping, isMobileDevice, theme),
	}
})

const expandedCategories = ref<Set<string>>(new Set())

const recommendedPlan = computed(() => {
	const { plans } = pageContent.comparison
	return Object.values(plans).find(plan => plan.recommended)
})

const shouldHideBasicColumn = computed(() => {
	return theme === 'shopping'
})

const getFeatureValue = (
	feature: ComparisonFeature,
	planType: 'buyShipPlus' | 'basic',
) => {
	const value = feature[planType]
	if (typeof value === 'boolean') {
		return { type: 'boolean', value }
	}
	return { type: 'text', value: value || '-' }
}

const toggleCategory = (categoryId: string) => {
	if (expandedCategories.value.has(categoryId)) {
		expandedCategories.value.delete(categoryId)
	} else {
		expandedCategories.value.add(categoryId)

		// GA tracking: Only trigger when expanding
		createGAEvent(GaGeneralEvent.ButtonClick, {
			page_name: getCurrentGaPageName(),
			button_name: 'Expand - Comparison',
		})
	}
}

const isCategoryExpanded = (categoryId: string) => {
	return expandedCategories.value.has(categoryId)
}

const isLastFeature = (categoryIndex: number, featureIndex: number) => {
	const category = pageContent.comparison.categories[categoryIndex]
	const isLastInCategory = featureIndex === category.features.length - 1
	const isLastCategory =
		categoryIndex === pageContent.comparison.categories.length - 1
	return isLastInCategory && isLastCategory
}

const getTooltipConfig = (content: string) => ({
	content,
	placement: 'top' as const,
	popperClass: 'max-w-[300px] whitespace-pre-line !z-0 bg-black rounded-lg',
	autoHide: true,
	distance: 8,
	triggers: ['click'] as const,
})

onMounted(() => {
	if (isMobile.value && pageContent.comparison.categories.length > 0) {
		expandedCategories.value.add(pageContent.comparison.categories[0].id)
	}
})
</script>

<template>
	<section class="bg-white">
		<div class="mx-auto flex flex-col gap-acquaintances">
			<header class="bs-container">
				<div class="col-span-12">
					<div class="flex flex-col gap-acquaintances">
						<h2 class="typography-h2-str text-center text-n-950">
							{{ pageContent.comparison.title }}
						</h2>
						<BaseAttentionBox>
							<template #description>
								{{ pageContent.comparison.subtitle }}
							</template>
						</BaseAttentionBox>
					</div>
				</div>
			</header>

			<main :class="[{ 'bs-container': !isMobile }]">
				<div :class="[{ 'col-span-12': !isMobile }]">
					<div class="relative" :class="{ 'mt-[28px]': theme === 'shipping' }">
						<div
							v-if="
								recommendedPlan &&
								pageContent.comparison.settings.highlightRecommended
							"
							:class="`absolute left-0 right-0 top-0 grid ${config.layout.gridCols} pointer-events-none`"
						>
							<div></div>
							<!-- Place Recommended label in the second column -->
							<div class="relative">
								<div :class="config.classes.recommended">
									{{ recommendedPlan.badge?.text || 'Recommended' }}
								</div>
							</div>
							<div v-if="!config.layout.hideBasic"></div>
						</div>

						<div
							class="overflow-hidden border border-x-0 border-n-200 bg-white mdp:rounded-xl mdp:border"
						>
							<!-- Table headers -->
							<div
								class=""
								:class="`typography-s1-reg grid ${config.layout.gridCols}`"
							>
								<div :class="config.classes.featureHeader"></div>
								<div :class="config.classes.buyShipPlusHeader">
									<h4 class="typography-s2-str text-n-950">
										{{ pageContent.comparison.plans.buyShipPlus.name }}
									</h4>
									<p class="typography-s2-reg text-n-950">
										{{ pageContent.comparison.plans.buyShipPlus.price }}
									</p>
								</div>
								<div
									v-if="!config.layout.hideBasic"
									:class="config.classes.basicHeader"
								>
									<h4 class="typography-s2-str text-n-950">
										{{ pageContent.comparison.plans.basic.name }}
									</h4>
									<p class="typography-s2-reg text-n-950">
										{{ pageContent.comparison.plans.basic.price }}
									</p>
								</div>
							</div>

							<!-- Table content -->
							<div
								v-for="(category, categoryIndex) in pageContent.comparison
									.categories"
								:key="category.id"
							>
								<!-- Category headers -->
								<template v-if="isMobile">
									<!-- Mobile: Category headers span across columns -->
									<div
										:class="`grid ${config.layout.gridCols} border-t ${config.colors.border}`"
									>
										<h5
											:class="`${config.classes.categoryFeature} ${
												config.layout.hideBasic ? 'col-span-2' : 'col-span-3'
											}`"
										>
											{{ category.name }}
											<BaseIcon
												v-if="
													category.hasInfo &&
													pageContent.comparison.settings.showTooltips
												"
												v-tooltip="getTooltipConfig(category.infoContent || '')"
												name="info_v2"
												class="fill-n-300eee ml-8px h-20px w-20px cursor-pointer"
											/>
											<!-- Expand/collapse button -->
											<button
												@click="toggleCategory(category.id)"
												class="ml-auto flex items-center justify-end"
											>
												<BaseIcon
													:name="
														isCategoryExpanded(category.id)
															? 'minus_v1'
															: 'add_v1'
													"
													class="h-24px w-24px fill-n-950"
												/>
											</button>
										</h5>
									</div>
								</template>

								<!-- Desktop: Original category header layout -->
								<div
									v-else
									:class="`grid ${config.layout.gridCols} border-t ${config.colors.border}`"
								>
									<!-- Category name -->
									<h5 :class="config.classes.categoryFeature">
										{{ category.name }}
										<BaseIcon
											v-if="
												category.hasInfo &&
												pageContent.comparison.settings.showTooltips
											"
											v-tooltip="getTooltipConfig(category.infoContent || '')"
											name="info_v2"
											class="ml-8px h-20px w-20px cursor-pointer fill-n-300"
										/>
									</h5>

									<!-- Middle column (Buy&Ship Plus column) -->
									<div :class="config.classes.categoryMiddle"></div>

									<!-- Third column -->
									<div
										v-if="!config.layout.hideBasic"
										:class="config.classes.categoryBasic"
									></div>
								</div>

								<!-- Feature items -->
								<div
									v-for="(feature, featureIndex) in category.features"
									:key="feature.id"
									v-show="isCategoryExpanded(category.id) || !isMobile"
									class="bg-white"
								>
									<div :class="`grid ${config.layout.gridCols}`">
										<!-- Feature name -->
										<div :class="config.classes.featureItem">
											<div class="">
												<span class="break-all leading-relaxed">
													{{ feature.label }}
												</span>
											</div>
											<BaseIcon
												v-if="
													feature.hasTooltip &&
													pageContent.comparison.settings.showTooltips
												"
												v-tooltip="
													getTooltipConfig(feature.tooltipContent || '')
												"
												name="info_v2"
												class="mt-1 h-20px w-20px flex-shrink-0 cursor-pointer fill-n-300"
											/>
										</div>

										<!-- Buy&Ship Plus column -->
										<div
											:class="
												isLastFeature(categoryIndex, featureIndex)
													? config.classes.buyShipPlusFeatureLast
													: config.classes.buyShipPlusFeature
											"
										>
											<template
												v-if="
													getFeatureValue(feature, 'buyShipPlus').type ===
													'boolean'
												"
											>
												<!-- Check Icon -->
												<BaseIcon
													v-if="getFeatureValue(feature, 'buyShipPlus').value"
													name="success_circle2"
													class="h-24px w-24px fill-g-500"
												/>
											</template>
											<div
												v-else
												class="flex flex-col gap-8px"
												:class="featureItemFontClass"
											>
												{{ getFeatureValue(feature, 'buyShipPlus').value }}
												<div
													v-if="feature.note"
													class="typography-c2-reg text-n-400"
												>
													{{ feature.note }}
												</div>
											</div>
										</div>

										<!-- Basic column - only shown when not hidden -->
										<div
											v-if="!config.layout.hideBasic"
											:class="config.classes.basicFeature"
										>
											<template
												v-if="
													getFeatureValue(feature, 'basic').type === 'boolean'
												"
											>
												<!-- Check Icon -->
												<BaseIcon
													v-if="getFeatureValue(feature, 'basic').value"
													name="success_circle2"
													class="h-24px w-24px fill-g-500"
												/>
												<!-- Dash for false -->
												<div v-else class="text-n-400">-</div>
											</template>
											<div v-else class="text-n-950">
												{{ getFeatureValue(feature, 'basic').value }}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</main>

			<footer
				class="bs-container typography-c2-reg whitespace-pre-line text-start text-n-400"
			>
				<div class="col-span-12">
					<p
						v-for="(line, index) in pageContent.comparison.disclaimer.lines"
						:key="index"
						v-html="line"
					></p>
				</div>
			</footer>
		</div>
	</section>
</template>
